import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Row, Spinner, Table } from "react-bootstrap";
import SetPageTitle from "../../../components/SetPageTitle";
import BonusChart from "./BonusChart";
import { useTranslation } from "react-i18next";
import {
  useGetCareerDataMutation,
  useGetProductionMonthListQuery,
  // useGetProfileQuery,
} from "../../../app/features/apiSlice";
import Loader from "../../../components/Loader";
import { useParams } from "react-router-dom";
import {
  compensationTableData,
  selectColourStyles2,
  sortProductionMonthaData,
  toastError,
} from "../../../helpers";
import Select from "react-select";
import { isWithinInterval, parseISO } from "date-fns";

const Career = () => {
  const { t } = useTranslation();
  const allTimeLabel = t('allTime')
  const sessionId = useRef(Date.now()).current;
  const { lang } = useParams();
  const [collabData, setCollabData] = useState([]);
  const [qualificationLevel, setQualificationLevel] = useState("")
  const [totalCollab, setTotalCollab] = useState("");
  const [docPer, setDocPer] = useState({});
  const [total, setTotal] = useState({
    currentMonth: 0,
    progressiveDoc: 0,
  });
  const [date, setDate] = useState({});
  const [options, setOptions] = useState([]);
  const [selOption, setSelOption] = useState({});
  // const { data: loggedUser } = useGetProfileQuery({ sessionId });

  const productionMonth = useGetProductionMonthListQuery({ sessionId });

  const [getCareerData, { isLoading }] = useGetCareerDataMutation();
  const handleGetCareerData = async () => {
    try {
      const response = await getCareerData({ date, lang });
      setQualificationLevel(response.data.qualificationLevel)
      setTotalCollab(response?.data?.totalCollaborators - 1)
      const docPerData = response?.data?.data.filter(
        (item) => item.first_name === "DOC"
      );
      const otherThanDoc = response?.data?.data.filter(
        (item) => item.first_name !== "DOC"
      );
      const currentMonth = response?.data?.data.reduce(
        (sum, user) => sum + user.currentMonthDoc,
        0
      );
      const progressiveDoc = response?.data?.data.reduce(
        (sum, user) => sum + user.updated_doc,
        0
      );
      if (docPerData.length > 0) {
        setDocPer(docPerData[0]);
      }
      if (otherThanDoc.length > 0) {
        setCollabData(otherThanDoc);
      }
      setTotal({ currentMonth, progressiveDoc });
    } catch (err) {
      toastError(err, t("error.serverNotResponding"));
    }
  };

  useEffect(() => {
    if (date.month && date.year) {
      handleGetCareerData();
    }
    // eslint-disable-next-line
  }, [date]);

  const handleSetDate = (dateString) => {
    if (dateString) {
      const [month, year] = dateString.split(" ");
      setSelOption({
        value: `${month} ${year}`,
        label: `${month} ${year}`,
      });
      setDate({ month, year });
    } else {
      setSelOption({
        value: ``,
        label: allTimeLabel,
      });
      setDate({ month: "all", year: "all" });
    }
  };

  useEffect(() => {
    if (productionMonth.isSuccess) {
      if (productionMonth.data.length > 0) {
        const sortedData = sortProductionMonthaData(productionMonth.data);
        const currentDate = new Date();

        // Find current production month
        const currentProductionMonth = sortedData.find((data) => {
          const fromDate = parseISO(data.from_date);
          const toDate = parseISO(data.to_date);
          return isWithinInterval(currentDate, { start: fromDate, end: toDate });
        });

        const defaultOption = currentProductionMonth || sortedData[0];
        const optionData = sortedData.map((data) => ({
          value: `${data.month} ${data.year}`,
          label: `${data.month} ${data.year}`,
        }));
        setDate({ month: sortedData[0].month, year: sortedData[0].year });
        setDate({ month: defaultOption.month, year: defaultOption.year });
        setSelOption({
          value: `${defaultOption.month} ${defaultOption.year}`,
          label: `${defaultOption.month} ${defaultOption.year}`,
        });

        const finalOptionData = [
          { value: "", label: allTimeLabel },
          ...optionData
        ]
        setOptions(finalOptionData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionMonth.isSuccess, productionMonth.data]);

  return (
    <>
      <Card bg="dark1 text-light mb-3">
        <Card.Header>
          <SetPageTitle title="career" />
        </Card.Header>
        <Card.Body>
          <Row>
            <Col className="align-self-end my-2">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th className="text-light">{t("careerLevel")}</th>
                    <th className="text-light">{t("totalVolumeDOC")}</th>
                    <th className="text-light">{t("maxNumberDOCs")}</th>
                  </tr>
                </thead>
                <tbody>
                  {compensationTableData.map((data, i) => (
                    <tr key={i}>
                      <td className="text-light">{data.level}</td>
                      <td className="text-light">{data.doc}</td>
                      <td className="text-light">{data.maxDoc}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
            <Col className="my-2">
              <h6 className="text-center">
                {t("teamManagerCompensationPlan")}
              </h6>
              <BonusChart />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card bg="dark1 text-light mb-3">
        <Card.Header>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">

            {/* Left section */}
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2 w-100">
              <SetPageTitle title={t("currentCareerLevel")} />

              {qualificationLevel && (
                <span className="text-uppercase mb-0">
                  {t("yourCareerLevel")}: {qualificationLevel}
                </span>
              )}

              {totalCollab > 0 && (
                <span className="text-uppercase mb-0">
                  {`${t("total")} ${t("sidebar.collaborators")}`}: {totalCollab}
                </span>
              )}
            </div>

            {/* Right section */}
            <div className="d-flex align-items-center gap-2 w-100 w-md-auto">
              {productionMonth.isLoading && <Spinner size="sm" />}

              {productionMonth.isSuccess && (
                <div className="w-100" style={{ maxWidth: "220px" }}>
                  <Select
                    options={options}
                    styles={selectColourStyles2}
                    value={selOption}
                    onChange={(e) => handleSetDate(e.value)}
                    placeholder={t("sltLabel")}
                  />
                </div>
              )}
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <h6 className="mt-3">{t("directLines")}</h6>
          {isLoading && <Loader />}
          <Table className="career" responsive>
            <thead>
              <tr>
                <th></th>
                {collabData.map((item, i) => (
                  <th key={i}>
                    {item.first_name} {item.last_name}
                  </th>
                ))}
                <th>DOC Pers</th>
                <th>{t("total")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="bg-success">
                  {t("DOCVolume")} <br />
                  {t("currentMonth")}
                </td>
                {collabData.map((item, i) => (
                  <td key={i}>{item.currentMonthDoc || 0}</td>
                ))}
                <td className={"tbl-bg-dark px-0"}>
                  <div>{docPer.currentMonthDoc || 0}</div>
                </td>
                <td>{total.currentMonth}</td>
              </tr>
              <tr className="add-space">
                <td className="bg-success">
                  {t("ProgressiveDOCVolume")} <br />
                  {t("with50Rule")}
                </td>
                {collabData.map((item, i) => (
                  <td key={i}>{item.taken_from_collab || 0}</td>
                ))}
                <td className={"tbl-bg-dark px-0"}>
                  <div>{docPer.total_doc_amount || 0}</div>
                </td>
                <td>
                  {collabData.reduce(
                    (sum, item) => sum + (item.taken_from_collab || 0),
                    0
                  ) + (docPer.total_doc_amount || 0)}
                </td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
};

export default Career;
